import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import fetcher from '../../../fetcher'
import { useVinDecode } from '../../../hooks/useVinDecode'
import { usePersonDetailStore } from '../stores/personDetailStore'
import { allYearOptions, toOptionStrings, useCascadeOptions, VehicleSelect } from './vehicleSelects'
import { keepEscapeInOpenSelect } from '../selectEscape'
import { toAmount } from './ProfitBreakdown'
import './AddCarModal.scss'

const OWNERSHIP = [['LEASE', 'Lease'], ['FINANCE', 'Finance'], ['OWNED_OUTRIGHT', 'Owned Outright']]
const PURCHASE_MODES = [['combine', 'Combine with Sale'], ['separate', 'Separate Deal']]

// A car the customer already owns is a used car, so the VIN decoder and the
// catalog both read the used side. MarkSoldModal stays on the new-car catalog —
// it records a car being sold today.
const IS_USED = true

const EMPTY = {
    vin: '', year: '', make: '', model: '', trim: '',
    ownershipType: '', expirationDate: '', mileage: '', payoff: '', notes: '',
    // Purchase mode only; never part of the car's payload.
    profit: '',
}

// A new car starts as a lease; an edit keeps whatever is stored, blank included.
const NEW_CAR = { ...EMPTY, ownershipType: 'LEASE' }

// Blank must survive as "unknown", never as 0 — Number('') is 0, so a plain
// cast would silently record a zero odometer or a settled loan.
const toNumber = (raw) => {
    const digits = String(raw ?? '').replace(/[^0-9.]/g, '')
    if (!digits) return null
    const n = Number(digits)
    return Number.isFinite(n) ? n : null
}

const trimmed = (value) => String(value || '').trim()

// VINs are capitals, so the field holds exactly what gets saved.
const toVin = (value) => String(value || '').toUpperCase()

// Mileage and payoff commas are display only: toNumber strips them before
// submit. Mileage keeps digits; payoff keeps digits and one decimal point.
const mileageChars = (value) => String(value ?? '').replace(/\D/g, '')
const payoffChars = (value) => {
    const [whole, ...decimals] = String(value ?? '').replace(/[^\d.]/g, '').split('.')
    return decimals.length ? `${whole}.${decimals.join('')}` : whole
}

// Purchase profit may be negative: payoff's characters plus a leading "-".
const profitChars = (value) => {
    const raw = String(value ?? '')
    return (raw.trimStart().startsWith('-') ? '-' : '') + payoffChars(raw)
}

// Commas go in the whole-number part only, after any "-".
const withCommas = (chars) => chars.replace(/^-?\d+/, (whole) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, ','))

// Where a caret that sat after n kept characters lands once commas are redrawn.
const caretAfterChars = (formatted, n) => {
    let i = 0
    for (let seen = 0; seen < n; i++) {
        if (formatted[i] !== ',') seen++
    }
    return i
}

// On create, only the keys the user filled in: the backend accepts {person}
// alone, so anything untouched is left out rather than defaulted.
//
// On edit the rule inverts. A PATCH reads an omitted key as "leave this alone",
// so emptying a field would silently do nothing — every field is therefore sent
// explicitly, blank ones as '' / null, which is what actually clears them.
const buildPayload = (f, { isEdit = false } = {}) => {
    const payoff = toNumber(f.payoff)
    const all = {
        vin: trimmed(f.vin),
        make: trimmed(f.make),
        model: trimmed(f.model),
        trim: trimmed(f.trim),
        ownership_type: f.ownershipType || '',
        year: f.year ? Number(f.year) : null,
        // Already the ISO yyyy-mm-dd the backend DateField wants — no MDY step.
        expiration_date: f.expirationDate || null,
        mileage: toNumber(f.mileage),
        payoff: payoff === null ? null : String(payoff),
        notes: trimmed(f.notes),
    }
    if (isEdit) return all
    // A real 0 survives — it is neither '' nor null.
    return Object.fromEntries(Object.entries(all).filter(([, v]) => v !== '' && v !== null))
}

// An existing car back into form values. Numbers become strings because the
// inputs and selects are text; null becomes '' so the field renders empty.
const toForm = (vehicle) => ({
    vin: toVin(vehicle.vin),
    year: vehicle.year != null ? String(vehicle.year) : '',
    make: vehicle.make || '',
    model: vehicle.model || '',
    trim: vehicle.trim || '',
    ownershipType: vehicle.ownership_type || '',
    expirationDate: vehicle.expiration_date || '',
    mileage: withCommas(mileageChars(vehicle.mileage)),
    payoff: withCommas(payoffChars(vehicle.payoff)),
    notes: vehicle.notes || '',
})

// `vehicle` switches the modal into edit mode: same form, prefilled, saving with
// a PATCH instead of a POST.
// `lockVehicle` (a sold car) locks the sale's Ownership and Year/Make/Model/Trim
// and hands the rest to `onSave`; `onViewDeal` + `detailsLabel` add a deal link.
// `purchase` ({ targets: [{id, label}], onRecord }) is Purchase Vehicle: every
// vehicle field locked, plus the recording mode and profit, which go to
// `onRecord` as { mode, target, amount }.
// `readOnly` (a Lead History trade-in) shows a historical snapshot: every field
// locked and nothing to save.
// `tradeIn` ({ onSave, onEditDeal }) edits a completed deal's trade-in snapshot:
// every field open, the whole snapshot handed to `onSave`, and — when opened
// from Lead History — a link to Edit Deal, where the car itself is replaced or
// removed.
export const AddCarModal = ({ open, onClose, vehicle = null, lockVehicle = false, onSave, onViewDeal, detailsLabel, purchase, readOnly = false, tradeIn }) => {
    const createVehicle = usePersonDetailStore((s) => s.createVehicle)
    const updateVehicle = usePersonDetailStore((s) => s.updateVehicle)
    const isEdit = Boolean(vehicle)
    const locked = lockVehicle || Boolean(purchase) || readOnly
    // A sold car's own details stay editable, so they lock separately.
    const frozen = Boolean(purchase) || readOnly
    const canCombine = Boolean(purchase?.targets?.length)
    const [f, setF] = useState(EMPTY)
    const [saving, setSaving] = useState(false)
    // Stops a second click that lands before the disabled button renders.
    const savingRef = useRef(false)
    // No default mode, like Ownership, unless there is no sale to combine with.
    const [buy, setBuy] = useState({ mode: '', target: '' })

    useEffect(() => {
        if (!open) return
        setF(vehicle ? { ...EMPTY, ...toForm(vehicle) } : NEW_CAR)
        setSaving(false)
        savingRef.current = false
        setBuy({ mode: canCombine ? '' : 'separate', target: '' })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, vehicle?.id])

    const set = (patch) => setF((prev) => ({ ...prev, ...patch }))

    // Rewriting a typed value drops the caret at the end; put it back after
    // render, as NewLeadModal's phone does. Unconditional: a rejected key still
    // re-renders.
    const caretRef = useRef(null)
    useLayoutEffect(() => {
        const held = caretRef.current
        if (!held) return
        caretRef.current = null
        held.input.setSelectionRange(held.start, held.end)
    })

    // Uppercasing never changes the length, so the selection goes back as-is.
    const onVinChange = (e) => {
        const { value, selectionStart, selectionEnd } = e.target
        caretRef.current = { input: e.target, start: selectionStart, end: selectionEnd }
        set({ vin: toVin(value) })
    }

    // Mileage and payoff: keep what `chars` allows, redraw the commas, and hold
    // the caret after the same characters.
    const onCommaFieldChange = (key, chars) => (e) => {
        let raw = e.target.value
        let caret = e.target.selectionStart ?? raw.length

        // A deleted comma would just come back, so take the digit beside it
        // instead: after it for Delete, before it otherwise.
        if (raw.length < f[key].length && chars(raw) === chars(f[key])) {
            if (e.nativeEvent.inputType === 'deleteContentForward') {
                raw = raw.slice(0, caret) + raw.slice(caret + 1)
            } else if (caret > 0) {
                raw = raw.slice(0, caret - 1) + raw.slice(caret)
                caret -= 1
            }
        }
        // A second decimal point would shift the decimals; drop the one typed.
        if (raw[caret - 1] === '.' && raw.indexOf('.') !== raw.lastIndexOf('.')) {
            raw = raw.slice(0, caret - 1) + raw.slice(caret)
            caret -= 1
        }

        const value = withCommas(chars(raw))
        const at = caretAfterChars(value, chars(raw.slice(0, caret)).length)
        caretRef.current = { input: e.target, start: at, end: at }
        set({ [key]: value })
    }

    // An edit opens with a VIN already in the field; decoding it would overwrite
    // the Year/Make/Model/Trim the rep had corrected by hand. Only a VIN that
    // differs from the one the modal opened with is worth decoding — compared
    // in capitals, so a lowercase VIN that opens uppercased is not a change.
    // A locked car's Year/Make/Model/Trim can't take a decode, so none is made.
    const vinChanged = toVin(f.vin) !== toVin(vehicle?.vin)
    const decode = useVinDecode(open && vinChanged && !locked ? f.vin : '', { isUsed: IS_USED })

    // Fill the vehicle from a decode exactly once per decoded VIN. The effect
    // keys on the result object, which only changes when a new decode lands, so
    // edits the user makes afterwards are never overwritten. The ref also tells
    // the cascade below whether the decode has been applied yet.
    const appliedRef = useRef(null)
    useEffect(() => {
        const result = decode.result
        if (!result || appliedRef.current === result) return
        appliedRef.current = result
        set({
            year: result.year,
            make: result.make,
            model: result.model,
            // Empty when the VIN matched several trims — the user finishes the
            // decode from the Trim select, which is populated from the same
            // response below.
            trim: result.trim,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [decode.result])

    const decoded = decode.result
    // The decode's values reach the form through the effect above, so for one
    // render the fields still describe the previous car. Fetching then would
    // issue three catalog requests for a vehicle about to be replaced, so the
    // cascade holds until the decode has landed.
    const settling = Boolean(decoded) && appliedRef.current !== decoded
    // A level's options come from the decode for as long as the level still
    // holds the value the decode produced; edit it and the level fetches again.
    const decodedList = (level, key) =>
        (decoded && !settling && decoded[level] === f[level]) ? toOptionStrings(decoded[key]) : null

    // Locked selects only show their value, so they fetch no option lists.
    const makes = useCascadeOptions(
        decodedList('year', 'makes'),
        open && !locked && Boolean(f.year) && !settling,
        () => fetcher.makesForYear(IS_USED),
        // Not f.year: /cars/?is_used= carries no year, so every year would
        // re-issue the identical request.
        [open, Boolean(f.year), settling, decoded])
    const models = useCascadeOptions(
        decodedList('make', 'models'),
        open && !locked && Boolean(f.year && f.make) && !settling,
        () => fetcher.modelsForMake(f.year, f.make, IS_USED),
        [open, f.year, f.make, settling, decoded])
    // When the VIN matched several trims the decode is unresolved, and those
    // candidates are a better list than every trim of the model. A decode that
    // resolved cleanly still offers the full list, so a wrong guess stays
    // correctable — the VIN prefills, the user decides.
    const trims = useCascadeOptions(
        decodedList('model', decoded?.candidates?.length > 1 ? 'candidates' : 'trims'),
        open && !locked && Boolean(f.year && f.make && f.model) && !settling,
        () => fetcher.trimsForModel(f.year, f.make, f.model, IS_USED),
        [open, f.year, f.make, f.model, settling, decoded])

    const canRecord = buy.mode === 'separate' || (buy.mode === 'combine' && buy.target !== '')

    const submit = async () => {
        if (savingRef.current || (purchase && !canRecord)) return
        savingRef.current = true
        setSaving(true)
        try {
            const payload = buildPayload(f, { isEdit })
            if (tradeIn) await tradeIn.onSave(payload)
            else if (purchase) {
                await purchase.onRecord({
                    mode: buy.mode,
                    ...(buy.mode === 'combine' && { target: Number(buy.target) }),
                    amount: toAmount(f.profit),
                })
            } else if (lockVehicle) {
                const { vin, mileage, payoff, notes } = payload
                await onSave({ vin, expirationDate: f.expirationDate, mileage, payoff, notes })
            } else if (isEdit) await updateVehicle(vehicle.id, payload)
            else await createVehicle(payload)
            onClose()
        } catch (e) {
            /* toast handled in the store; the modal stays open with its values */
        } finally {
            savingRef.current = false
            setSaving(false)
        }
    }

    let title = purchase ? 'Purchase Vehicle' : isEdit ? 'Edit Car' : 'Add Car'
    if (readOnly || tradeIn) title = 'Trade-in Vehicle'
    const sub = readOnly || tradeIn ? 'As it was when traded in.' : purchase
        ? 'Record the purchase of this vehicle. It leaves Current Vehicles.'
        : `${isEdit ? 'Update this vehicle.' : 'Add a vehicle.'} All fields are optional.`
    let cta = isEdit ? 'Save Changes' : 'Add Car'
    if (purchase) cta = saving ? 'Recording…' : 'Record Purchased'
    else if (saving) cta = isEdit ? 'Saving…' : 'Adding…'

    return (
        <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ className: `add-car-modal${locked ? ' is-locked' : ''}`, onKeyDown: keepEscapeInOpenSelect }}>
            <div className="modal-head">
                <div>
                    <div className="modal-title">{title}</div>
                    <div className="modal-sub">{sub}</div>
                    {onViewDeal && detailsLabel && (
                        <button type="button" className="details-link" onClick={onViewDeal}>{detailsLabel}</button>
                    )}
                    {tradeIn?.onEditDeal && (
                        <button type="button" className="details-link" onClick={tradeIn.onEditDeal}>Replace or remove in Edit Deal</button>
                    )}
                </div>
                <button className="modal-x" onClick={onClose} aria-label="Close">✕</button>
            </div>

            <div className="modal-body">
                <div className="mrow">
                    <div className="mfield">
                        <div className="mfield-label">VIN</div>
                        <input
                            aria-label="VIN"
                            value={f.vin}
                            maxLength={17}
                            disabled={frozen}
                            placeholder={frozen ? undefined : 'Enter VIN'}
                            onChange={onVinChange}
                        />
                    </div>
                    <div className="mfield">
                        <div className="mfield-label">Ownership</div>
                        <select
                            aria-label="Ownership"
                            value={f.ownershipType}
                            disabled={locked}
                            onChange={(e) => set({ ownershipType: e.target.value })}
                        >
                            {/* Only an older car saved without ownership shows this. */}
                            <option value="" disabled hidden>Ownership</option>
                            {OWNERSHIP.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                    </div>
                </div>
                {decode.loading && <div className="vin-hint">Decoding VIN…</div>}
                {decode.error && (
                    <div className="vin-hint is-error">
                        We couldn’t decode this VIN. You can enter the vehicle manually.
                    </div>
                )}

                <div className="section-label">Vehicle Details</div>
                <div className="mrow">
                    <VehicleSelect
                        showLabel label="Year" value={f.year} options={allYearOptions()} disabled={locked}
                        onChange={(year) => set({ year, make: '', model: '', trim: '' })}
                    />
                    <VehicleSelect
                        showLabel label="Make" value={f.make} options={makes} disabled={locked || !f.year}
                        onChange={(make) => set({ make, model: '', trim: '' })}
                    />
                </div>
                <div className="mrow">
                    <VehicleSelect
                        showLabel label="Model" value={f.model} options={models} disabled={locked || !f.make}
                        onChange={(model) => set({ model, trim: '' })}
                    />
                    <VehicleSelect
                        showLabel label="Trim" value={f.trim} options={trims} disabled={locked || !f.model}
                        onChange={(trim) => set({ trim })}
                    />
                </div>

                <div className="mrow">
                    <div className="mfield is-date">
                        <div className="mfield-label">Expiration Date</div>
                        <input
                            type="date"
                            aria-label="Expiration Date"
                            value={f.expirationDate}
                            disabled={frozen}
                            onChange={(e) => set({ expirationDate: e.target.value })}
                        />
                    </div>
                    <div className="mfield is-short">
                        <div className="mfield-label">Mileage</div>
                        <input
                            aria-label="Mileage"
                            inputMode="numeric"
                            value={f.mileage}
                            disabled={frozen}
                            placeholder={frozen ? undefined : 'e.g. 25,000'}
                            onChange={onCommaFieldChange('mileage', mileageChars)}
                        />
                    </div>
                    <div className="mfield is-short">
                        <div className="mfield-label">Payoff</div>
                        {/* "$" is display only; the label lets a click on it focus the input. */}
                        <label className="money-input">
                            <span className="money-prefix" aria-hidden="true">$</span>
                            <input
                                aria-label="Payoff"
                                inputMode="decimal"
                                value={f.payoff}
                                disabled={frozen}
                                placeholder={frozen ? undefined : 'e.g. 12,500.00'}
                                onChange={onCommaFieldChange('payoff', payoffChars)}
                            />
                        </label>
                    </div>
                </div>

                <div className="mfield">
                    <div className="mfield-label">Notes</div>
                    <textarea
                        aria-label="Notes"
                        rows={3}
                        value={f.notes}
                        disabled={frozen}
                        placeholder={frozen ? undefined : 'Add notes…'}
                        onChange={(e) => set({ notes: e.target.value })}
                    />
                </div>

                {purchase && (
                    <>
                        <div className="section-label">Purchase</div>
                        <div className="mfield-label">Record as</div>
                        <div className="seg" role="group" aria-label="Record as">
                            {PURCHASE_MODES.map(([value, label]) => (
                                <button
                                    type="button"
                                    key={value}
                                    className={`seg-opt${buy.mode === value ? ' active' : ''}`}
                                    aria-pressed={buy.mode === value}
                                    disabled={value === 'combine' && !canCombine}
                                    onClick={() => setBuy((b) => ({ ...b, mode: value }))}
                                >{label}</button>
                            ))}
                        </div>
                        {!canCombine && (
                            <div className="vin-hint">No completed sale to combine with, so this is recorded as its own deal.</div>
                        )}
                        <div className="mrow">
                            {buy.mode === 'combine' && (
                                <div className="mfield">
                                    <div className="mfield-label">Combine with sale</div>
                                    <select
                                        aria-label="Combine with sale"
                                        value={buy.target}
                                        onChange={(e) => setBuy((b) => ({ ...b, target: e.target.value }))}
                                    >
                                        <option value="" disabled hidden>Select a sale</option>
                                        {purchase.targets.map((t) => <option key={t.id} value={String(t.id)}>{t.label}</option>)}
                                    </select>
                                </div>
                            )}
                            {/* Alone on its row, it keeps VIN's half width. */}
                            <div className={`mfield ${buy.mode === 'combine' ? 'is-short' : 'is-half'}`}>
                                <div className="mfield-label">Profit</div>
                                <label className="money-input">
                                    <span className="money-prefix" aria-hidden="true">$</span>
                                    <input
                                        aria-label="Profit"
                                        inputMode="decimal"
                                        value={f.profit}
                                        placeholder="e.g. 1,500.00"
                                        onChange={onCommaFieldChange('profit', profitChars)}
                                    />
                                </label>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="modal-foot">
                {readOnly ? (
                    <button className="btn-add" onClick={onClose}>Close</button>
                ) : (
                    <>
                        {!purchase && <span className="foot-note">All fields are optional.</span>}
                        <button className="btn-ghost" onClick={onClose}>Cancel</button>
                        <button className="btn-add" onClick={submit} disabled={saving || (purchase && !canRecord)}>
                            {cta}
                        </button>
                    </>
                )}
            </div>
        </Dialog>
    )
}
